/**
 * Blog post JSON-LD — BreadcrumbList + Article (+ FAQPage).
 *
 * ONE builder for both places the markup is written:
 *   - pages/buyer/BlogPost.js passes it to useSEO at runtime, and
 *   - scripts/inject-seo-heads.js writes it into the static head at build time,
 *     which is the only copy a crawler that never runs JS (every AI crawler) sees
 *     on any post the prerender skips.
 *
 * Two independent writers is exactly how this broke. The page emitted `Article`
 * with a Person/Organization author; the injector separately emitted a
 * `BlogPosting` that hardcoded an Organization author, and its dedupe guard
 * looked only for BlogPosting, so it never fired. All 133 posts shipped both:
 * 112 disagreed on the author's name, and on 21 a named person was typed as a
 * company. Keep this the single source.
 *
 * scripts/inject-seo-heads.js loads THIS file by stripping the `export` keywords
 * and requiring it, so keep every declaration a plain top-level `export function`
 * and keep the file free of imports.
 */

/**
 * 112 of the 133 posts are bylined "Buy Sell Taxi Team". Schema.org allows an
 * Organization author, which is what a team byline is. A Person — with
 * credentials — is emitted only where a real individual is named.
 */
export function blogAuthor(post) {
  const BASE_URL = 'https://www.buyselltaxi.com';
  if (!post.author || post.author === 'Buy Sell Taxi Team') {
    return { '@type': 'Organization', name: 'Buy Sell Taxi', url: BASE_URL };
  }
  return {
    '@type': 'Person',
    name: post.author,
    jobTitle: 'Commercial Vehicle Expert',
    // Only Aditya Kumar's bio, and only on his posts — attaching it to any
    // other byline recreates the name/description mismatch.
    ...(post.author === 'Aditya Kumar'
      ? { description: 'Aditya Kumar is an entrepreneur and commercial vehicle expert based in New Delhi with deep expertise in the Delhi NCR taxi and fleet market.' }
      : {}),
    url: `${BASE_URL}/about`,
    worksFor: { '@type': 'Organization', name: 'Buy Sell Taxi', url: BASE_URL },
  };
}

/**
 * CTR experiment, started 2026-10-11. The AITP post ranks ~5.0 on 53K
 * impressions/28d but only 0.27% click: its FAQPage hands search engines and
 * AI Overviews the exact answer ("₹10,000–₹15,000, valid 5 years") in
 * machine-readable form. Posts listed here keep their visible FAQ section but
 * ship no FAQPage markup. Read the result ~2026-11-08 (28 days, compare CTR at
 * a similar position, not clicks) against the baseline in
 * scripts/gsc-baseline-aitp-faq-2026-10-11.md before touching any other post.
 */
export const FAQ_SCHEMA_EXPERIMENT_OFF = ['aitp-all-india-tourist-permit-delhi'];

/**
 * @param {object} post   a blogPosts.js entry (needs slug, title, publishedAt)
 * @param {string} image  absolute image URL; the generic OG image if omitted
 * @param {object} [hub]  { name, path } when the post belongs to a hub (the
 *                        T-permit guide): the breadcrumb then runs through the
 *                        hub instead of the generic /blog index
 */
export function blogJsonLd(post, image, hub) {
  const BASE_URL = 'https://www.buyselltaxi.com';
  const postUrl = `${BASE_URL}/blog/${post.slug}`;
  const ld = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        hub
          ? { '@type': 'ListItem', position: 2, name: hub.name, item: `${BASE_URL}${hub.path}` }
          : { '@type': 'ListItem', position: 2, name: 'Blog', item: `${BASE_URL}/blog` },
        { '@type': 'ListItem', position: 3, name: post.title, item: postUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.metaDescription || post.excerpt,
      image: [image || `${BASE_URL}/og-image.jpg`],
      url: postUrl,
      datePublished: post.publishedAt,
      dateModified: post.updatedAt || post.publishedAt,
      author: blogAuthor(post),
      publisher: {
        '@type': 'Organization',
        name: 'Buy Sell Taxi',
        url: BASE_URL,
        logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo192.png` },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': postUrl },
    },
  ];
  if (
    Array.isArray(post.faqs) && post.faqs.length > 0
    && !FAQ_SCHEMA_EXPERIMENT_OFF.includes(post.slug)
  ) {
    ld.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: post.faqs.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    });
  }
  return ld;
}
